import { MIN_VOLUME_DB } from './fades'
import type {
  AudioItem,
  SceneItem,
  TimelineDoc,
  TimelineItem,
  TrackType,
  VideoItem,
  ViewItem,
} from './types'

/**
 * A generation-3 document (#541, #566) as the layers render it: every
 * sourced scene and its views become the media items the Editor Starter's
 * layers already play. Pure, and run where the layers are mounted
 * (`TimelineComposition`), never at hydration: the editor loads the hydrated
 * document as its state and must keep the scenes it edits, and the render's
 * length is measured on the scenes themselves, so a title-card scene counts.
 *
 * - **The sound plays once, from the scene's first audible view.** That view
 *   becomes a video item with the scene's id, window, volume and sound
 *   fades: the same item a one-view scene was before scenes existed, so it
 *   plays the file the picture comes from (`VideoLayer`). Every other view is
 *   a video item of the same window with no sound. A view is audible when its
 *   track is neither hidden nor muted.
 * - **A scene no audible view shows plays as an audio item**, on the scenes
 *   track, with the scene's id (an audiogram's recording, or a scene whose
 *   views are all on hidden or muted tracks).
 * - **Followers find the sound where they look**: the item with the scene's
 *   id is the one that plays it.
 * - The scenes track's own `hidden` or `muted` silences the scene wherever
 *   its sound ends up.
 *
 * A title-card scene (no source) and a view whose scene is missing are left
 * as they are; they draw nothing. A document without scenes comes back as
 * itself.
 */
export function lowerScenes(doc: TimelineDoc): TimelineDoc {
  const trackOf = new Map<string, TrackType>()
  const viewsOf = new Map<string, ViewItem[]>()
  let hasScenes = false
  for (const track of doc.tracks)
    for (const id of track.items) {
      trackOf.set(id, track)
      const item = doc.items[id]
      if (item?.type === 'scene') hasScenes = true
      if (item?.type === 'view') {
        const views = viewsOf.get(item.zealous.sceneId) ?? []
        views.push(item)
        viewsOf.set(item.zealous.sceneId, views)
      }
    }
  if (!hasScenes) return doc

  const items: Record<string, TimelineItem> = { ...doc.items }
  // Track item ids to rewrite: a carrier view's id becomes its scene's.
  const renamed = new Map<string, string>()
  // Scene ids whose sound moved onto a view: they leave the scenes track.
  const moved = new Set<string>()

  for (const scene of Object.values(doc.items)) {
    if (scene.type !== 'scene' || scene.assetId === undefined) continue
    const sceneTrack = trackOf.get(scene.id)
    const silenced = !!(sceneTrack?.hidden || sceneTrack?.muted)
    const views = viewsOf.get(scene.id) ?? []
    const carrier = views.find((view) => {
      const track = trackOf.get(view.id)
      return !track?.hidden && !track?.muted
    })
    if (!carrier) {
      items[scene.id] = audioOf(scene)
    } else {
      moved.add(scene.id)
      renamed.set(carrier.id, scene.id)
      delete items[carrier.id]
    }
    for (const view of views) {
      const isCarrier = view === carrier
      const id = isCarrier ? scene.id : view.id
      items[id] = videoOf(scene, view, {
        id,
        silent: !isCarrier || silenced,
      })
    }
  }

  const tracks = doc.tracks.map((track) => ({
    ...track,
    items: track.items
      .filter((id) => !moved.has(id))
      .map((id) => renamed.get(id) ?? id),
  }))
  return { ...doc, tracks, items }
}

function audioOf(scene: SceneItem): AudioItem {
  return {
    id: scene.id,
    type: 'audio',
    from: scene.from,
    durationInFrames: scene.durationInFrames,
    isDraggingInTimeline: false,
    top: scene.top,
    left: scene.left,
    width: scene.width,
    height: scene.height,
    opacity: 1,
    audioStartFromInSeconds: scene.audioStartFromInSeconds ?? 0,
    decibelAdjustment: scene.decibelAdjustment,
    playbackRate: scene.playbackRate,
    audioFadeInDurationInSeconds: scene.audioFadeInDurationInSeconds,
    audioFadeOutDurationInSeconds: scene.audioFadeOutDurationInSeconds,
    assetId: scene.assetId!,
    zealous: mediaZealousOf(scene),
  }
}

function videoOf(
  scene: SceneItem,
  view: ViewItem,
  { id, silent }: { id: string; silent: boolean },
): VideoItem {
  return {
    id,
    type: 'video',
    // The scene's span: a view's is derived from it, and the window is its.
    from: scene.from,
    durationInFrames: scene.durationInFrames,
    isDraggingInTimeline: false,
    top: view.top,
    left: view.left,
    width: view.width,
    height: view.height,
    opacity: view.opacity,
    rotation: view.rotation,
    borderRadius: view.borderRadius,
    cropLeft: view.cropLeft,
    cropTop: view.cropTop,
    cropRight: view.cropRight,
    cropBottom: view.cropBottom,
    videoStartFromInSeconds: scene.audioStartFromInSeconds ?? 0,
    decibelAdjustment: silent ? MIN_VOLUME_DB : scene.decibelAdjustment,
    playbackRate: scene.playbackRate,
    audioFadeInDurationInSeconds: scene.audioFadeInDurationInSeconds,
    audioFadeOutDurationInSeconds: scene.audioFadeOutDurationInSeconds,
    fadeInDurationInSeconds: view.fadeInDurationInSeconds,
    fadeOutDurationInSeconds: view.fadeOutDurationInSeconds,
    assetId: scene.assetId!,
    keepAspectRatio: view.keepAspectRatio,
    ...(view.fit ? { fit: view.fit } : {}),
    ...(view.backdrop ? { backdrop: view.backdrop } : {}),
    zealous: mediaZealousOf(scene),
  }
}

function mediaZealousOf(scene: SceneItem): VideoItem['zealous'] {
  const { source, sourceEnd, highlightId, seconds } = scene.zealous
  return {
    source: source!,
    ...(sourceEnd !== undefined ? { sourceEnd } : {}),
    ...(highlightId ? { highlightId } : {}),
    ...(seconds ? { seconds } : {}),
  }
}
